// src/pages/locations/components/form/useLocationForm.ts
import React from "react";

import { apiFieldErrors, type FieldErrors } from "@/pages/locations/formErrors";

export type FormValues = Record<string, string>;

/**
 * Form plumbing shared by the Country / Region / City forms: values, dirty
 * tracking, validation on submit (then live, once the user has seen an error),
 * server field errors, and focusing the first invalid control.
 */
export function useLocationForm<T extends FormValues>(params: {
  initial: T;
  validate: (values: T) => FieldErrors;
  /** Field ids are `${idPrefix}-${field}` — used to focus the first error. */
  idPrefix: string;
  /** Raw mutation error; 422 field errors are surfaced on the inputs. */
  error?: unknown;
  onValid: (values: T, options: { addAnother: boolean }) => void;
}) {
  const { initial, validate, idPrefix, error, onValid } = params;

  const [values, setValues] = React.useState<T>(initial);
  const [errors, setErrors] = React.useState<FieldErrors>({});
  const [submitted, setSubmitted] = React.useState(false);
  const [baseline, setBaseline] = React.useState<T>(initial);

  const formRef = React.useRef<HTMLFormElement>(null);

  const serverErrors = React.useMemo(() => apiFieldErrors(error), [error]);

  const isDirty = React.useMemo(
    () => Object.keys(baseline).some((key) => values[key] !== baseline[key]),
    [values, baseline]
  );

  /** Updates several fields at once — dependent fields must move together. */
  const setFields = React.useCallback(
    (patch: Partial<T>) => {
      const updated = { ...values, ...patch } as T;
      setValues(updated);

      // Only re-validate live after a failed submit, so the form does not
      // shout at someone still filling in the first field.
      if (submitted) setErrors(validate(updated));
    },
    [submitted, validate, values]
  );

  const setField = React.useCallback(
    (field: keyof T & string, next: string) => {
      setFields({ [field]: next } as Partial<T>);
    },
    [setFields]
  );

  const errorFor = React.useCallback(
    (field: keyof T & string): string | null =>
      errors[field] ?? serverErrors[field] ?? null,
    [errors, serverErrors]
  );

  const handleSubmit = React.useCallback(
    (addAnother = false) => {
      setSubmitted(true);

      const nextErrors = validate(values);
      setErrors(nextErrors);

      const firstInvalid = Object.keys(nextErrors)[0];
      if (firstInvalid) {
        const control = formRef.current?.querySelector<HTMLElement>(
          `#${idPrefix}-${firstInvalid}`
        );
        control?.focus();
        control?.scrollIntoView({ block: "center", behavior: "smooth" });
        return;
      }

      onValid(values, { addAnother });
    },
    [idPrefix, onValid, validate, values]
  );

  /** Used by "Save & add another" to clear the form for the next record. */
  const resetTo = React.useCallback((next: T) => {
    setValues(next);
    setBaseline(next);
    setErrors({});
    setSubmitted(false);
  }, []);

  return {
    values,
    setField,
    setFields,
    errorFor,
    isDirty,
    formRef,
    handleSubmit,
    resetTo,
  };
}
